import React, { useState, useMemo, useEffect } from "react";
import { Badge } from "../ui/Badge";
import { ArrowUp, ArrowDown, SquarePen, Save, X } from "lucide-react";
import axios from "axios";
import { Toaster, toast } from "react-hot-toast";


const AssetDataTable = ({ tableOverview, getTypeColor, itemsPerPage = 10, id, setEditMetadata }) => {
  // Sorting state
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableData, setEditableData] = useState([]);
  const [activePopup, setActivePopup] = useState(null);
  const [editedRows, setEditedRows] = useState(new Set());


  // Column search state
  const [columnSearch, setColumnSearch] = useState({
    name: "",
    type: "",
    is_cde: "",
    metadata: "",
    businessterm: "",
    transformationLogic: "",
    sample: "",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Handle sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  // Handle column search change
  const handleColumnSearchChange = (column, value) => {
    setColumnSearch((prev) => ({
      ...prev,
      [column]: value,
    }));
    // Reset to first page when searching
    setCurrentPage(1);
  };

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    if (!tableOverview || !Array.isArray(tableOverview)) return [];

    let filtered = [...tableOverview];

    // Apply column filters
    Object.entries(columnSearch).forEach(([column, searchValue]) => {
      if (searchValue && searchValue.trim()) {
        filtered = filtered.filter((row) => {
          let cellValue = "";

          if (column === "is_cde") {
            cellValue = row[column] ? "yes" : "no";
          } else {
            cellValue = row[column] || "";
          }

          return String(cellValue)
            .toLowerCase()
            .includes(searchValue.toLowerCase().trim());
        });
      }
    });

    // Apply sorting
    if (sortColumn) {
      filtered.sort((a, b) => {
        let aVal = a[sortColumn];
        let bVal = b[sortColumn];

        // Handle boolean for is_cde
        if (sortColumn === "is_cde") {
          aVal = aVal ? "yes" : "no";
          bVal = bVal ? "yes" : "no";
        }

        // Handle null/undefined
        if (aVal === null || aVal === undefined) aVal = "";
        if (bVal === null || bVal === undefined) bVal = "";

        // String comparison
        const comparison = String(aVal)
          .toLowerCase()
          .localeCompare(String(bVal).toLowerCase());

        return sortOrder === "asc" ? comparison : -comparison;
      });
    }

    return filtered;
  }, [tableOverview, columnSearch, sortColumn, sortOrder]);

  // Pagination calculations
  const totalPages = Math.max(
    1,
    Math.ceil(filteredAndSortedData.length / itemsPerPage),
  );
  const validCurrentPage = Math.min(currentPage, totalPages);
  const indexOfFirstItem = (validCurrentPage - 1) * itemsPerPage;
  const indexOfLastItem = Math.min(
    indexOfFirstItem + itemsPerPage,
    filteredAndSortedData.length,
  );
  const currentData = filteredAndSortedData.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  useEffect(() => {
    if (tableOverview) {
      const enriched = tableOverview.map((row) => ({
        ...row,
        glossary: row.glossary || "", // new column
      }));
      setEditableData(enriched);
    }
  }, [tableOverview]);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

 

  const handleFieldChange = (index, field, value) => {
    setEditableData((prev) => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
  
    // Track edited row using attribute_id (stable key)
    const id = editableData[index]?.attribute_id;
    if (id) {
      setEditedRows((prev) => new Set(prev).add(id));
    }
  };
  

  const handleSave = async () => {
    const modifiedRows = editableData.filter(row =>
      editedRows.has(row.attribute_id)
    );
  
    if (modifiedRows.length === 0) {
      setIsEditMode(false);
      return;
    }
  
    const columnsData = modifiedRows.map((row) => ({
      column_name: row?.name || "N/A",
      is_cde: row?.is_cde || false,
      technical_description: row?.metadata,
      business_description: row?.businessterm,
      transformation_logic: row?.transformationLogic,
    }));
  
    const payload = { columns: columnsData };
  
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/assets/${id}/metadata`, payload);  
      setEditedRows(new Set());
      setIsEditMode(false);
      toast.success("Metadata Saved successfully.");
      setEditMetadata((prev) => !prev)
    } catch (err) {
      console.error(err);
      toast.success("Meta Save Failed/.");
    }
  };
  

  // Render pagination buttons
  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(
      1,
      validCurrentPage - Math.floor(maxVisiblePages / 2),
    );
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    buttons.push(
      <button
        key="prev"
        className="px-3 py-1 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        disabled={validCurrentPage === 1}
        onClick={() => handlePageChange(validCurrentPage - 1)}
      >
        Previous
      </button>,
    );

    if (startPage > 1) {
      buttons.push(
        <button
          key={1}
          className="px-3 py-1 text-[10px] border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 transition-colors"
          onClick={() => handlePageChange(1)}
        >
          1
        </button>,
      );
      if (startPage > 2) {
        buttons.push(
          <span key="ellipsis1" className="px-3 py-1 text-[10px] text-gray-500">
            ...
          </span>,
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          className={`px-3 py-1 text-[10px] border rounded-md transition-colors ${
            validCurrentPage === i
              ? "bg-[#bb1ccc] text-white border-[#bb1ccc] shadow-sm"
              : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
          }`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>,
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(
          <span key="ellipsis2" className="px-3 py-2 text-sm text-gray-500">
            ...
          </span>,
        );
      }
      buttons.push(
        <button
          key={totalPages}
          className="px-3 py-1 text-[10px] border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 transition-colors"
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>,
      );
    }

    buttons.push(
      <button
        key="next"
        className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        disabled={validCurrentPage === totalPages}
        onClick={() => handlePageChange(validCurrentPage + 1)}
      >
        Next
      </button>,
    );

    return buttons;
  };

  // Column configuration
  const columns = [
    { key: "name", label: "Field Name" },
    { key: "type", label: "Type" },
    { key: "is_cde", label: "CDE" },
    { key: "metadata", label: "Technical Metadata" },
    { key: "1", label: "Business Description" },
    { key: "transformationLogic", label: "Transformation Logic" },
    { key: "glossary", label: "Glossary" },
    { key: "sample", label: "Sample Value" },
  ];

  return (
    <div>
      {
        localStorage.getItem("role") === "Admin"  && (
        <div className="flex justify-end gap-2 mb-3 px-4">
        {isEditMode ? (
  <button
    className="h-[40px] text-[#bb1ccc] hover:text-white transition-colors duration-200 border border-[#bb1ccc] p-2 rounded-lg flex items-center gap-2 hover:bg-[#bb1ccc] hover:shadow-lg px-4"
    onClick={() => {
      setEditableData(tableOverview.map((row) => ({ ...row, glossary: row.glossary || "" })));
      setEditedRows(new Set());
      setIsEditMode(false);
    }}
  >
    <X className="h-5" />
    Cancel
  </button>
) : (
  <button
    className="h-[40px] text-[#bb1ccc] hover:text-white transition-colors duration-200 border border-[#bb1ccc] p-2 rounded-lg flex items-center gap-2 hover:bg-[#bb1ccc] hover:shadow-lg px-4"
    onClick={() => setIsEditMode(true)}
  >
    <SquarePen className="h-5" />
    Edit
  </button>
)}
 

        <button
          disabled={!isEditMode}
          className="h-[40px] text-[#bb1ccc] disabled:text-gray-200 hover:text-white transition-colors duration-200 border border-[#bb1ccc] disabled:border-gray-200 disabled:cursor-not-allowed p-2 rounded-lg flex items-center gap-2 hover:bg-[#bb1ccc] disabled:bg-gray-500 hover:shadow-lg px-4 "
          onClick={handleSave}
        >
          <Save className="h-5" />
          Save
        </button>
      </div>
        )
      }
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            {/* Table Header - Same UI as IncidentTableHead */}
            <thead className="bg-[#9897FF1A] text-[#404040] font-normal">
              {/* Header Row with Sort */}
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className="px-3 pb-[6px] pr-[2px] text-[12px] pt-[10px] text-left whitespace-nowrap w-[14%] cursor-pointer select-none font-normal"
                  >
                    <span className="flex items-center gap-1">
                      {col.label}
                      {sortColumn === col.key ? (
                        sortOrder === "asc" ? (
                          <ArrowUp size={12} />
                        ) : (
                          <ArrowDown size={12} />
                        )
                      ) : (
                        <ArrowUp size={12} className="opacity-30" />
                      )}
                    </span>
                  </th>
                ))}
              </tr>

              {/* Search Row */}
              <tr>
                {columns.map((col) => (
                  <th
                    key={`search-${col.key}`}
                    className="px-3 pt-0 pb-[10px] w-[14px] font-normal"
                  >
                    <input
                      className="w-full text-[11px] pb-[5px] pl-[10px] placeholder-gray-400 bg-white rounded py-2 outline-none font-normal placeholder:font-normal"
                      placeholder={col.key === "is_cde" ? "Yes/No" : "Search"}
                      value={columnSearch[col.key] || ""}
                      onChange={(e) =>
                        handleColumnSearchChange(col.key, e.target.value)
                      }
                    />
                  </th>
                ))}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="divide-y divide-gray-200 bg-white">
              {currentData.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="py-8 text-center text-gray-500"
                  >
                    No data found matching your search criteria
                  </td>
                </tr>
              ) : (
                currentData.map((attr, index) => (
                  <tr
                    key={attr.attribute_id || index}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-2 px-3 text-[11px]">
                      <div className="font-normal text-gray-900">
                        {attr.name}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-[11px]">
                      <Badge variant={getTypeColor(attr.type)} size="sm">
                        {attr.type}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 text-[11px]">
                      {isEditMode ? (
                        <div className="relative w-24">
                          <select
                            value={editableData[index]?.is_cde ? "Yes" : "No"}
                            onChange={(e) =>
                              handleFieldChange(
                                index,
                                "is_cde",
                                e.target.value === "Yes",
                              )
                            }
                            className="
                              w-full
                              text-[11px]
                              px-2 py-1.5
                              border border-gray-300
                              rounded-md
                              bg-white
                              text-gray-700
                              shadow-sm
                              focus:outline-none focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc]
                              hover:border-gray-400
                              transition-all duration-150
                              appearance-none
                              cursor-pointer
                            "
                          >
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                          </select>

                          {/* Tailwind Arrow */}
                          <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-gray-400">
                            <svg
                              className="h-[15px] w-[15px]"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.24 4.5a.75.75 0 01-1.08 0l-4.24-4.5a.75.75 0 01.02-1.06z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                      ) : (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${
                            attr.is_cde
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {attr.is_cde ? "Yes" : "No"}
                        </span>
                      )}
                    </td>

                    {/* <td className="py-2 px-3 text-[11px]">{attr.is_cde ? "Yes" : "No"}</td> */}
                    <td className="py-2 px-3 text-[11px]">
                      {isEditMode ? (
                        <div className="flex items-center gap-1">
                          {/* Existing Input — unchanged */}
                          <input
                            type="text"
                            value={editableData[index]?.metadata || ""}
                            onChange={(e) =>
                              handleFieldChange(
                                index,
                                "metadata",
                                e.target.value,
                              )
                            }
                            placeholder="Enter metadata"
                            className="
                                w-full
                                text-[11px]
                                px-2 py-1.5
                                border border-gray-300
                                rounded-md
                                bg-white
                                text-gray-700
                                shadow-sm
                                placeholder-gray-400
                                focus:outline-none focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc]
                                hover:border-gray-400
                                transition-all duration-150
                              "
                          />

                          {/* Expand Button (your same expand icon usage) */}
                          <button
                            type="button"
                            onClick={() =>
                              setActivePopup({ index, field: "metadata" })
                            }
                            className="px-2 text-gray-500 hover:text-gray-700"
                            title="Expand"
                          >
                            ⤢
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-600 text-[11px]">
                          {attr.metadata || "—"}
                        </span>
                      )}
                    </td>

                    {/* <td className="py-2 px-3 text-[11px] text-gray-600">
                    {attr.metadata || "No description available"}
                  </td> */}
                    <td className="py-2 px-3 text-[11px]">
                      {isEditMode ? (
                        <div className="flex gap-1">
                          <input
                            value={editableData[index]?.businessterm || ""}
                            onChange={(e) =>
                              handleFieldChange(
                                index,
                                "businessterm",
                                e.target.value,
                              )
                            }
                            className="
                                w-full
                                text-[11px]
                                px-2 py-1.5
                                border border-gray-300
                                rounded-md
                                bg-white
                                text-gray-700
                                shadow-sm
                                placeholder-gray-400
                                focus:outline-none focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc]
                                hover:border-gray-400
                                transition-all duration-150
                              "
                          />
                          <button
                            onClick={() =>
                              setActivePopup({ index, field: "businessterm" })
                            }
                          >
                            ⤢
                          </button>
                        </div>
                      ) : (
                        attr.businessterm
                      )}
                    </td>

                    {/* <td className="py-2 px-3 text-[11px] text-gray-600">
                    {attr.businessterm || "No description available"}
                  </td> */}

                    <td className="py-2 px-3 text-[11px]">
                      {isEditMode ? (
                        <div className="flex gap-1">
                          <input
                            value={
                              editableData[index]?.transformationLogic || ""
                            }
                            onChange={(e) =>
                              handleFieldChange(
                                index,
                                "transformationLogic",
                                e.target.value,
                              )
                            }
                            className="
                              w-full
                              text-[11px]
                              px-2 py-1.5
                              border border-gray-300
                              rounded-md
                              bg-white
                              text-gray-700
                              shadow-sm
                              placeholder-gray-400
                              focus:outline-none focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc]
                              hover:border-gray-400
                              transition-all duration-150
                            "
                          />
                          <button
                            onClick={() =>
                              setActivePopup({
                                index,
                                field: "transformationLogic",
                              })
                            }
                          >
                            ⤢
                          </button>
                        </div>
                      ) : (
                        attr.transformationLogic ? attr.transformationLogic : "N/A"
                      )}
                    </td>

                   

                    <td className="py-2 px-3 text-[11px]">
                      {isEditMode ? (
                        <div className="relative">
                          <select
                          disabled
                            value={editableData[index]?.glossary || ""}
                            onChange={(e) =>
                              handleFieldChange(
                                index,
                                "glossary",
                                e.target.value,
                              )
                            }
                            className="
                              w-full
                              text-[11px]
                              px-2 py-1.5
                              border border-gray-300
                              rounded-md
                              bg-gray-200
                              cursor-not-allowed
                              text-gray-700
                              shadow-sm
                              focus:outline-none focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc]
                              hover:border-gray-400
                              transition-all duration-150
                              appearance-none
                            "
                          >
                            <option value="">Select</option>
                            <option value="A">A</option>
                            <option value="B">B</option>
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-gray-400">
                            <svg
                              className="h-[15px] w-[15px]"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.24 4.5a.75.75 0 01-1.08 0l-4.24-4.5a.75.75 0 01.02-1.06z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                      ) : (
                        attr.glossary || "N/A"
                      )}
                    </td>

                    <td className="py-2 px-3 text-[11px] text-gray-600">
                      {attr.sample || "N/A"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {activePopup && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
    <div className="bg-white w-[640px] max-w-[92%] rounded-lg shadow-xl border border-gray-200">
      
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="text-sm font-medium text-gray-700">
          Edit {activePopup.field}
        </h3>

        <button
          onClick={() => setActivePopup(null)}
          className="text-gray-400 hover:text-gray-600 text-lg leading-none"
        >
          <X className="h-5" />
        </button>
      </div>

      {/* Body */}
      <div className="p-4">
        <textarea
          value={editableData[activePopup.index][activePopup.field] || ""}
          onChange={(e) =>
            handleFieldChange(
              activePopup.index,
              activePopup.field,
              e.target.value
            )
          }
          className="
            w-full h-64 resize-none
            text-sm text-gray-700
            border border-gray-300
            rounded-md
            p-3
            placeholder-gray-400
            focus:outline-none focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc]
          "
          placeholder="Enter detailed information..."
        />
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
        <button
          onClick={() => setActivePopup(null)}
          className="px-4 py-1.5 text-sm border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100"
        >
          Cancel
        </button>

        <button
          onClick={() => setActivePopup(null)}
          className="px-4 py-1.5 text-sm bg-[#bb1ccc] text-white rounded-md hover:bg-[#bb1ccc] shadow-sm"
        >
          Done
        </button>
      </div>
    </div>
  </div>
)}

        </div>

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 py-2 px-3 border-t border-gray-200 bg-white">
            <div className="text-sm text-gray-600">
              Showing{" "}
              <span className="font-normal">
                {filteredAndSortedData.length > 0 ? indexOfFirstItem + 1 : 0}
              </span>{" "}
              to <span className="font-normal">{indexOfLastItem}</span> of{" "}
              <span className="font-normal">
                {filteredAndSortedData.length}
              </span>{" "}
              results
              {tableOverview &&
                filteredAndSortedData.length !== tableOverview.length && (
                  <span className="text-gray-400 ml-2">
                    (filtered from {tableOverview.length} total)
                  </span>
                )}
            </div>
            <div className="flex space-x-1">{renderPaginationButtons()}</div>
          </div>
        )}
      </div>
      
    </div>
  );
};

export default AssetDataTable;
